'use client';
import React, { useState } from 'react';
import NewsPreview from '@/components/NewsPreview';
import type { FeedPreviewCard } from '@/lib/feed_preview';
import { getNewsFeed } from '@/actions/news';
import { FeedType } from '@/lib/enums';

type NewsWithLoadMoreProps = {
  type: FeedType;
  id: string;
  /** Slimmed on the server (lib/feed_preview.ts) — only what NewsPreview renders. */
  initialNews?: FeedPreviewCard[];
  offset?: string; // Optional prop for initial offset
  hasMore?: boolean; // Optional prop to indicate if there are more news items
  notHomepage?: boolean; // Optional prop to determine if it's not the homepage
  showLoadMore: boolean; // Optional prop to control the visibility of the load more button
};

const NewsWithLoadMore: React.FC<NewsWithLoadMoreProps> = ({
  showLoadMore,
  type,
  id,
  initialNews,
  offset: initialOffset,
  hasMore: initialHasMore,
}) => {
  const [news, setNews] = useState<FeedPreviewCard[]>(initialNews || []);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState<string | undefined>(initialOffset);
  const [hasMore, setHasMore] = useState(
    initialHasMore !== undefined ? initialHasMore : true,
  );

  const handleLoadMore = async () => {
    setLoading(true);
    try {
      const newNews = await getNewsFeed({
        type,
        feedId: id,
        offset,
        maxCards: 5,
      });
      if (!newNews || 'error' in newNews || !newNews.card_list) {
        console.error('Error fetching more news:', newNews);
        setLoading(false);
        return;
      }
      setNews((prev) => [...prev, ...(newNews.card_list || [])]);
      setOffset(newNews.min_card_id);
      setHasMore(newNews.reload_required || false);
    } catch (error) {
      console.error('Error fetching more news:', error);
    }
    setLoading(false);
  };
  return (
    <>
      <div className='w-full overflow-auto no-scrollbar scrollbar-hide'>
        <div className='w-[210%] sm:w-[210%] md:w-[150%] lg:w-full xl:w-full'>
          <div className='w-full grid grid-cols-3 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5'>
            {news.map((newsObject, index) => (
              <div key={index} className='flex-shrink-0 self-center w-full p-2'>
                <NewsPreview newsObject={newsObject} layout={'horizontal'} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className='col-span-full flex justify-center items-center'>
        {showLoadMore &&
          (hasMore ? (
            <button
              type='button'
              className='px-2 py-1 rounded-xl border-primary border-2 text-primary cursor-pointer'
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'View More'}
            </button>
          ) : (
            <span>No more news available</span>
          ))}
      </div>
    </>
  );
};

export default NewsWithLoadMore;
